import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { onPrimaryColor } from '@/styles/darkHero.ts';

import { dayjs } from '@/utils/dayjsSetup.js';

export interface TimeslotDay {
    date: string;
    count: number;
}

interface Props {
    days: TimeslotDay[];
    selectedDate?: string;
    onSelect: (date: string) => void;
    onPrevious?: () => void;
    onNext?: () => void;
    showControls?: boolean;
    testIdPrefix?: string;
}

const useStyles = makeStyles((theme: any) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        width: '100%',
    },
    monthLabel: {
        fontSize: '0.75rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.common.darkGrey,
        opacity: 0.6,
    },
    strip: {
        display: 'flex',
        alignItems: 'stretch',
        gap: '0.5rem',
        width: '100%',
    },
    days: {
        display: 'flex',
        gap: '0.5rem',
        flex: 1,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
    },
    day: {
        appearance: 'none',
        flex: '1 0 auto',
        minWidth: '4.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.125rem',
        padding: '0.5rem 0.75rem',
        fontFamily: 'inherit',
        background: theme.palette.common.white,
        border: theme.border.light,
        borderRadius: theme.radius.field,
        color: theme.palette.common.darkGrey,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        '&:hover:not(:disabled)': {
            borderColor: theme.palette.common.yellow,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: '2px',
        },
        '&:disabled': {
            cursor: 'default',
            opacity: 0.45,
        },
    },
    selected: {
        background: theme.palette.common.yellow,
        color: onPrimaryColor(theme),
        borderColor: theme.palette.common.yellow,
        '&:hover:not(:disabled)': {
            borderColor: theme.palette.common.yellow,
        },
    },
    weekday: {
        fontSize: '0.75rem',
        lineHeight: 1.2,
    },
    dayNumber: {
        fontSize: '1.125rem',
        fontWeight: theme.weight.heading,
        lineHeight: 1.2,
    },
    count: {
        fontSize: '0.6875rem',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    control: {
        appearance: 'none',
        flex: '0 0 auto',
        width: '2.25rem',
        background: theme.palette.common.white,
        border: theme.border.light,
        borderRadius: theme.radius.field,
        color: theme.palette.common.darkGrey,
        fontSize: '1rem',
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.common.yellow,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: '2px',
        },
    },
}));

export const TimeslotDayStrip = ({
    days,
    selectedDate,
    onSelect,
    onPrevious,
    onNext,
    showControls = true,
    testIdPrefix = 'booking',
}: Props) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    // the global dayjs locale is set in an App effect, which runs after this render
    const localizedDay = (date: string): Dayjs => dayjs(date).locale(i18n.language);

    const getMonthLabel = (): string => {
        if (days.length === 0) return '';
        const first = localizedDay(days[0].date);
        const last = localizedDay(days[days.length - 1].date);
        if (first.isSame(last, 'month')) return first.format('MMMM YYYY');
        if (first.isSame(last, 'year')) return `${first.format('MMMM')} – ${last.format('MMMM YYYY')}`;
        return `${first.format('MMMM YYYY')} – ${last.format('MMMM YYYY')}`;
    };
    const monthLabel = getMonthLabel();

    return (
        <div className={classes.container}>
            {monthLabel.length > 0 && (
                <Typography component="span" className={classes.monthLabel} data-testid={`${testIdPrefix}-month-label`}>
                    {monthLabel}
                </Typography>
            )}
            <div className={classes.strip}>
                {showControls && (
                    <button
                        type="button"
                        className={classes.control}
                        onClick={onPrevious}
                        aria-label={t('TimeslotPreviousDays')}
                        data-testid={`${testIdPrefix}-calendar-prev`}
                    >
                        ‹
                    </button>
                )}

                <div className={classes.days}>
                    {days.map((day) => {
                        const isSelected = day.date === selectedDate;
                        const dayDate = localizedDay(day.date);
                        return (
                            <button
                                key={day.date}
                                type="button"
                                disabled={day.count === 0}
                                aria-pressed={isSelected}
                                aria-label={`${dayDate.format('LL')}, ${t('TimeslotDayCount', {
                                    count: day.count,
                                })}`}
                                className={clsx(classes.day, isSelected && classes.selected)}
                                onClick={() => onSelect(day.date)}
                                data-testid={`${testIdPrefix}-day-${day.date}`}
                            >
                                <Typography component="span" className={classes.weekday}>
                                    {dayDate.format('dd')}
                                </Typography>
                                <Typography component="span" className={classes.dayNumber}>
                                    {dayDate.format('D')}
                                </Typography>
                                <Typography component="span" className={classes.count}>
                                    {t('TimeslotDayCount', { count: day.count })}
                                </Typography>
                            </button>
                        );
                    })}
                </div>

                {showControls && (
                    <button
                        type="button"
                        className={classes.control}
                        onClick={onNext}
                        aria-label={t('TimeslotNextDays')}
                        data-testid={`${testIdPrefix}-calendar-next`}
                    >
                        ›
                    </button>
                )}
            </div>
        </div>
    );
};
